import { Experiment } from "@amplitude/experiment-js-client"
import { withResolvers } from "@prismicio/editor-support/Promise"
import { use } from "@prismicio/editor-support/React"
import {
  type PropsWithChildren,
  Suspense,
  useEffect,
  useSyncExternalStore,
} from "react"
import { z } from "zod"

import { env } from "../env"
import { getRepositoryName } from "../routing"
import { trackExperimentExposure } from "../services/tracking"
import { useProfile } from "../services/user"

export function AmplitudeExperiments(props: PropsWithChildren) {
  return (
    <>
      <Suspense>
        <BlockingExperiments />
      </Suspense>
      {props.children}
    </>
  )
}

function BlockingExperiments() {
  const { shortId } = useProfile()

  useEffect(() => {
    experiment.start(shortId)
    return experiment.stop
  }, [shortId])

  return null
}

export function usePromptToSliceFlag(args: { active: boolean }) {
  const { active } = args
  const flag = useFeatureFlag("prompt-to-slice", active)
  return z.enum(["on", "off"]).catch("off").parse(flag)
}

export function useAiTranslationFlag(args: { active: boolean }) {
  const { active } = args
  const flag = useFeatureFlag("ai-translation", active)
  return z
    .enum(["on", "unlimited", "trial", "off"])
    .catch("off")
    .transform((value) => {
      if (value === "on") return "unlimited"
      return value
    })
    .parse(flag)
}

export function useAiTranslationFeedbackFormVariantFlag() {
  const flag = useFeatureFlag("ai-translation-feedback-form-variant")
  return z
    .enum(["per-word", "per-page"])
    .catch("per-page")
    .transform((value) => {
      if (value === "per-word") return "perWord"
      if (value === "per-page") return "perPage"
      return value
    })
    .parse(flag)
}

export function useGlossaryFlag() {
  const flag = useFeatureFlag("pb-glossary")
  return z.enum(["on", "off"]).catch("off").parse(flag)
}

export function useAiABMLandingPagesFlag(args: { active: boolean }) {
  const { active } = args
  const flag = useFeatureFlag("ai-abm-landing-pages", active)
  return z.enum(["on", "off"]).catch("off").parse(flag)
}

export function useAiProductLandingPagesFlag(args: { active: boolean }) {
  const { active } = args
  const flag = useFeatureFlag("ai-product-landing-pages", active)
  return z.enum(["on", "off"]).catch("off").parse(flag)
}

export function useRoleApiRolesFlag() {
  const flag = useFeatureFlag("pb-role-api-roles")
  return z.enum(["on", "off"]).catch("off").parse(flag)
}

export function useObelixAiTranslateDocumentFlag() {
  const flag = useFeatureFlag("pb-obelix-ai-translate-document")
  return z.enum(["on", "off"]).catch("off").parse(flag)
}

export function useReleasesTabFlag() {
  const flag = useFeatureFlag("pb-releases-tab")
  return z.enum(["on", "off"]).catch("off").parse(flag)
}

export function useMigrationReleaseNoModalFlag() {
  const flag = useFeatureFlag("migration-release-no-modal")
  return z.enum(["on", "off"]).catch("off").parse(flag)
}

export function useCollaborationRoomFlag() {
  const flag = useSuspenseFeatureFlag("pb-collaboration-room")
  return z.enum(["on", "off"]).catch("off").parse(flag)
}

export function useCollaborationCommentsFlag() {
  const flag = useFeatureFlag("pb-collaboration-comments")
  return z.enum(["on", "off"]).catch("off").parse(flag)
}

export function useCollaborationDataFlag() {
  const flag = useSuspenseFeatureFlag("pb-collaboration-data")

  // useCollaborationRoomFlag is a dependency of useCollaborationDataFlag.
  if (useCollaborationRoomFlag() === "off") return "off"

  return z.enum(["on", "off"]).catch("off").parse(flag)
}

export function useSharedOnboardingGuideFlag() {
  const flag = useFeatureFlag("shared-onboarding-new")
  return z
    .enum(["control", "with-shared-onboarding"])
    .catch("control")
    .parse(flag)
}

export function useAiSeoMetadataFlag() {
  const flag = useFeatureFlag("pb-ai-seo-metadata")
  return z.enum(["on", "off"]).catch("off").parse(flag)
}

export function useOnboardingDropOffFlag() {
  const flag = useSuspenseFeatureFlag("pb-onboarding-drop-off")
  return z.enum(["control", "videos", "upfront"]).catch("control").parse(flag)
}

export function useOnboardingFeedbackFlag() {
  // We need to access the payload of the experiment,
  // so we call directly experiment.variant.
  const flag = experiment.variant("pb-onboarding-feedback")

  return z
    .object({
      key: z.enum(["on", "off"]),
      value: z.enum(["on", "off"]),
      payload: z
        .object({
          idleTimeSeconds: z.record(z.string(), z.number().optional()),
          options: z.array(z.object({ id: z.string(), label: z.string() })),
        })
        .optional(),
    })
    .catch({
      key: "off",
      value: "off",
    })
    .parse(flag)
}

export function clearExperiments() {
  experiment.stop()
  experiment.clear()
}

/**
 * @param active Evaluating a flag (via calling experiment.variant) also produces an analytics event as a side effect.
 * We sometimes use this behavior to track how many users interacted with a feature once they saw it.
 * To prevent false negatives, we don't want to call experiment.variant if they can't see the feature yet.
 * One such case is Radix-based dialogs, which are always rendered even when not opened.
 */
function useFeatureFlag(flag: string, active = true) {
  return useSyncExternalStore(experiment.subscribe, () =>
    active ? experiment.variant(flag).value : undefined,
  )
}

/**
 * This is a variant of useFeatureFlag that suspends until the experiment is ready.
 * This is useful for features that cannot be rendered without the final experiment
 * value (e.g, weird flashing from content A to content B).
 */
function useSuspenseFeatureFlag(flag: string, active?: boolean) {
  use(experiment.waitForExperimentReady())

  return useFeatureFlag(flag, active)
}

/**
 * The Amplitude SDK is pretty bad and won't reliably publish new flag values when polling, so we do it ourselves.
 * Using useSyncExternalStore also has the benefit of isolating the rendering of each feature flag.
 */
function createExperimentStore() {
  let pollingId: number | undefined
  let listeners: Array<() => void> = []
  const experimentReady = withResolvers()

  const experiment = Experiment.initialize(
    env.PRISMIC_PAGE_BUILDER_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY,
    {
      serverUrl: new URL(
        "api",
        env.PRISMIC_AMPLITUDE_EXPERIMENT_SERVER_BASE_URL,
      ).href,
      flagsServerUrl: new URL(
        "flag",
        env.PRISMIC_AMPLITUDE_EXPERIMENT_SERVER_BASE_URL,
      ).href,
      exposureTrackingProvider: { track: trackExperimentExposure },
    },
  )

  function start(userId: string) {
    stop()

    async function poll() {
      try {
        await experiment.fetch({
          user_id: userId,
          groups: { Repository: [getRepositoryName()] },
        })
      } catch (error) {
        // If the experiment cannot be fetched, it's important to resolve the promise
        // so that `useSuspenseFeatureFlag` can be used.
        // Usage of reject would cause closest error boundary to be triggered and it's
        // the desired behavior for amplitude experiments.
        experimentReady.resolve()
        throw error
      }

      experimentReady.resolve()

      listeners.forEach((listener) => listener())
    }

    void poll()
    pollingId = window.setInterval(poll, 10_000)
  }

  function stop() {
    clearInterval(pollingId)
  }

  function clear() {
    experiment.clear()
  }

  function subscribe(listener: () => void) {
    listeners.push(listener)

    return function unsubscribe() {
      listeners = listeners.filter(
        (otherListener) => otherListener !== listener,
      )
    }
  }

  function variant(flag: string) {
    return experiment.variant(flag)
  }

  function waitForExperimentReady() {
    return experimentReady.promise
  }

  return {
    start,
    stop,
    clear,
    subscribe,
    variant,
    waitForExperimentReady,
  }
}

const experiment = createExperimentStore()
